/**
 * Calibre editor → Revisions (memo 5.2): this calibre's place in a revision line.
 *
 * Rendered only when the calibre's manufacturer has `supportsLineage` on — read
 * from the manufacturer DOCUMENT, because the catalogue the modal lists makers
 * from is an index projection that does not carry the flag.
 *
 * Writes are immediate (its own Save revision button), not part of the calibre
 * form's Save — the same contract as the Movement Family field.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { RevisionChipBadge } from './RevisionChipBadge';
import { DEFAULT_REVISION_TERMS, revisionTerms, type RevisionTerms } from '../../services/revisionTerms';
import { LineageReassignDialog } from './LineageReassignDialog';
import { ReassignByYearDialog } from './ReassignByYearDialog';
import { SplitFoldedAliasesDialog } from './SplitFoldedAliasesDialog';
import {
  getCustomCalibreById,
  getMovementManufacturerById,
  type CustomCalibreEntry,
} from '../../services/firebase';
import {
  getLineageMembers,
  getLineagesForManufacturer,
  saveLineage,
  setCalibreRevision,
  type CalibreLineageEntry,
} from '../../services/calibreLineage';
import {
  currentMember,
  foldedAliasCandidates,
  memberWarnings,
  parseYearInput,
  revisionChip,
} from '../../services/calibreLineageLogic';

interface Props {
  calibreId: string;
  manufacturerId: string;
  /**
   * The maker that originally made this calibre, when it differs (shared-types
   * 1.143.0). Its revision lines are offered too: ETA 6498-1 joins Unitas's
   * 6498 line. New lines are still created under the primary.
   */
  originalManufacturerId?: string;
  /** After a write that changes this calibre's aliases (split, AKA move). */
  onCalibreChanged?: () => void;
}

const NEW_LINE = '__new__';

interface Form {
  lineageId: string;
  newLabel: string;
  revisionLabel: string;
  from: string;
  until: string;
  isCurrent: boolean;
  isPlaceholder: boolean;
}

function formFrom(c: CustomCalibreEntry | null): Form {
  return {
    lineageId: c?.calibreLineageId ?? '',
    newLabel: '',
    revisionLabel: c?.revisionLabel ?? '',
    from: c?.activeFromYear != null ? String(c.activeFromYear) : '',
    until: c?.activeUntilYear != null ? String(c.activeUntilYear) : '',
    isCurrent: c?.isCurrent === true,
    isPlaceholder: c?.isLineagePlaceholder === true,
  };
}

export function CalibreRevisionsCard({ calibreId, manufacturerId, originalManufacturerId, onCalibreChanged }: Props) {
  const [gate, setGate] = useState<boolean | null>(null);
  const [terms, setTerms] = useState<RevisionTerms>(DEFAULT_REVISION_TERMS);
  const [calibre, setCalibre] = useState<CustomCalibreEntry | null>(null);
  const [lineages, setLineages] = useState<CalibreLineageEntry[]>([]);
  const [members, setMembers] = useState<CustomCalibreEntry[]>([]);
  const [form, setForm] = useState<Form>(formFrom(null));
  const [saving, setSaving] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignByYearOpen, setReassignByYearOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  /** Makers whose lines this calibre may join (supportsLineage on), primary first. */
  const [lineMakers, setLineMakers] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    setGate(null);
    if (!manufacturerId) {
      setGate(false);
      return;
    }
    const original = originalManufacturerId && originalManufacturerId !== manufacturerId ? originalManufacturerId : undefined;
    Promise.all([getMovementManufacturerById(manufacturerId), original ? getMovementManufacturerById(original) : null])
      .then(([m, o]) => {
        if (cancelled) return;
        const makers = [m, o].filter((x): x is NonNullable<typeof x> => x?.supportsLineage === true);
        // Terms follow the primary when it has lines, else the original maker.
        setTerms(revisionTerms(makers[0] ?? m));
        setLineMakers(makers.map((x) => ({ id: x.id, name: x.displayName || x.name || x.id })));
        setGate(makers.length > 0);
      })
      .catch(() => !cancelled && setGate(false));
    return () => {
      cancelled = true;
    };
  }, [manufacturerId, originalManufacturerId]);

  const reload = useCallback(async () => {
    const [c, ...lineSets] = await Promise.all([
      getCustomCalibreById(calibreId),
      ...lineMakers.map((mk) => getLineagesForManufacturer(mk.id)),
    ]);
    setCalibre(c as CustomCalibreEntry | null);
    setLineages((lineSets as CalibreLineageEntry[][]).flat());
    setForm(formFrom(c as CustomCalibreEntry | null));
  }, [calibreId, lineMakers]);
  const makerName = (id: string) => lineMakers.find((mk) => mk.id === id)?.name ?? id;

  useEffect(() => {
    if (gate) void reload().catch((err) => console.error('Failed to load revisions', err));
  }, [gate, reload]);

  const memberLineage = form.lineageId && form.lineageId !== NEW_LINE ? form.lineageId : '';
  useEffect(() => {
    if (!memberLineage) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    void getLineageMembers(memberLineage).then((ms) => !cancelled && setMembers(ms));
    return () => {
      cancelled = true;
    };
  }, [memberLineage, calibre]);

  const others = useMemo(() => members.filter((m) => m.id !== calibreId), [members, calibreId]);
  const otherCurrent = currentMember(others);
  const asMember = useMemo(
    () =>
      calibre
        ? {
            ...calibre,
            calibreLineageId: memberLineage || undefined,
            isCurrent: form.isCurrent && !form.isPlaceholder,
            isLineagePlaceholder: form.isPlaceholder,
            activeFromYear: parseYearInput(form.from),
            activeUntilYear: parseYearInput(form.until),
          }
        : null,
    [calibre, memberLineage, form],
  );
  const splitCandidates = calibre ? foldedAliasCandidates(calibre.name, calibre.alsoKnownAs ?? []) : [];

  if (!gate || !calibre || !asMember) return null;

  const chip = memberLineage
    ? revisionChip(asMember, asMember.isCurrent ? asMember : otherCurrent, [...others, asMember])
    : null;
  const warnings = memberLineage ? memberWarnings(asMember, others) : [];
  const badYear = (raw: string) => raw.trim() !== '' && parseYearInput(raw) === undefined;

  const handleSave = async () => {
    setSaving(true);
    try {
      let lineageId = form.lineageId;
      if (lineageId === NEW_LINE) {
        // Under the primary, unless only the original maker has lines turned on.
        lineageId = await saveLineage({ baseLabel: form.newLabel, manufacturerId: lineMakers[0]?.id ?? manufacturerId });
      }
      const activeFromYear = parseYearInput(form.from);
      const activeUntilYear = parseYearInput(form.until);
      // AI-applied years keep their stamp only while untouched; editing either
      // year hands both to the curator, and the AI guard then never changes them.
      const yearsUntouched =
        activeFromYear === calibre?.activeFromYear && activeUntilYear === calibre?.activeUntilYear;
      await setCalibreRevision(calibreId, {
        calibreLineageId: lineageId || null,
        revisionLabel: form.revisionLabel,
        activeFromYear,
        activeUntilYear,
        isCurrent: form.isCurrent,
        isLineagePlaceholder: form.isPlaceholder,
        ...(yearsUntouched && calibre?.activeDatesSource === 'ai'
          ? { activeDatesSource: 'ai' as const, activeDatesConfidence: calibre.activeDatesConfidence }
          : {}),
      });
      toast.success(lineageId ? 'Revision saved' : 'Removed from its revision line');
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';
  const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm';

  return (
    <div className="col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-gray-900">{terms.plural}</h4>
          {chip && <RevisionChipBadge chip={chip} />}
        </div>
        <div className="flex gap-1">
          {calibre.calibreLineageId && others.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setReassignOpen(true)}>
              Reassign brand-AKA refs…
            </Button>
          )}
          {calibre.calibreLineageId && others.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setReassignByYearOpen(true)}>
              Reassign by year…
            </Button>
          )}
          {splitCandidates.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setSplitOpen(true)}>
              Split folded aliases into separate revisions…
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={labelClass} htmlFor="rev-line">
            Revision line
          </label>
          <select
            id="rev-line"
            value={form.lineageId}
            onChange={(e) => setForm({ ...form, lineageId: e.target.value })}
            className={inputClass}
          >
            <option value="">— Not in a revision line —</option>
            {lineages.map((l) => (
              <option key={l.id} value={l.id}>
                {l.baseLabel}
                {l.manufacturerId !== manufacturerId ? ` (${makerName(l.manufacturerId)} line)` : ''}
              </option>
            ))}
            <option value={NEW_LINE}>+ New revision line…</option>
          </select>
          {form.lineageId === NEW_LINE && (
            <input
              value={form.newLabel}
              onChange={(e) => setForm({ ...form, newLabel: e.target.value })}
              placeholder="ETA Calibre 2824"
              aria-label="New revision line label"
              className={`${inputClass} mt-2`}
            />
          )}
        </div>

        {form.lineageId && (
          <>
            <div>
              <label className={labelClass} htmlFor="rev-label">
                Revision label
              </label>
              <input
                id="rev-label"
                value={form.revisionLabel}
                onChange={(e) => setForm({ ...form, revisionLabel: e.target.value })}
                placeholder="-2"
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass} htmlFor="rev-from">
                  Active from
                </label>
                <input
                  id="rev-from"
                  inputMode="numeric"
                  value={form.from}
                  onChange={(e) => setForm({ ...form, from: e.target.value })}
                  placeholder="1982"
                  className={`${inputClass} ${badYear(form.from) ? 'border-red-400' : ''}`}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="rev-until">
                  Active until
                </label>
                <input
                  id="rev-until"
                  inputMode="numeric"
                  value={form.until}
                  onChange={(e) => setForm({ ...form, until: e.target.value })}
                  placeholder="blank = still made"
                  className={`${inputClass} ${badYear(form.until) ? 'border-red-400' : ''}`}
                />
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.isCurrent && !form.isPlaceholder}
                disabled={form.isPlaceholder}
                onChange={(e) => setForm({ ...form, isCurrent: e.target.checked })}
              />
              <span>
                Current revision
                <span className="block text-xs text-gray-500">
                  {form.isPlaceholder
                    ? 'A placeholder can never be current.'
                    : otherCurrent && form.isCurrent
                      ? `Saving makes this current instead of ${otherCurrent.name}.`
                      : 'Set by you, never worked out from the years. One per line.'}
                </span>
              </span>
            </label>
            <label
              className="flex items-start gap-2 text-sm text-gray-700"
              title="Tick when this record is the bare line name (e.g. a hypothetical 'Seiko VK63') standing for 'revision unspecified', not a real movement. Leave it off for a real first revision such as the 1971 ETA 2824. A placeholder is never current, reads 'Revision unspecified', and appears in the Needs Assessment queue."
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.isPlaceholder}
                onChange={(e) => setForm({ ...form, isPlaceholder: e.target.checked, isCurrent: e.target.checked ? false : form.isCurrent })}
              />
              <span>
                Placeholder — revision unspecified
                <span className="block text-xs text-gray-500">
                  The bare name, not a real revision. Never current; listed in Needs Assessment.
                </span>
              </span>
            </label>
          </>
        )}
      </div>

      {warnings.map((w) => (
        <p key={w} className="text-xs text-amber-700">
          ⚠ {w}
        </p>
      ))}

      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => void handleSave()}
          loading={saving}
          disabled={
            badYear(form.from) || badYear(form.until) || (form.lineageId === NEW_LINE && !form.newLabel.trim())
          }
        >
          Save revision
        </Button>
      </div>

      {reassignOpen && (
        <LineageReassignDialog
          isOpen={reassignOpen}
          onClose={() => setReassignOpen(false)}
          calibre={calibre}
          siblings={members}
          onReassigned={() => {
            void reload();
            onCalibreChanged?.();
          }}
        />
      )}
      {reassignByYearOpen && (
        <ReassignByYearDialog
          isOpen={reassignByYearOpen}
          onClose={() => setReassignByYearOpen(false)}
          calibre={calibre}
          members={members.filter((m) => m.deprecated !== true)}
          onReassigned={() => {
            void reload();
            onCalibreChanged?.();
          }}
        />
      )}
      {splitOpen && (
        <SplitFoldedAliasesDialog
          isOpen={splitOpen}
          onClose={() => setSplitOpen(false)}
          calibreId={calibreId}
          manufacturerId={manufacturerId}
          lineages={lineages}
          onSplit={() => {
            void reload();
            onCalibreChanged?.();
          }}
        />
      )}
    </div>
  );
}
